import produce from 'immer';
import { action, makeObservable, observable, runInAction, toJS } from 'mobx';
import * as yup from 'yup';
import { NotificationManager } from '../components/notification-manager';
import { HttpMethod } from '../types';
import { Lang, LangRegEx } from '../utils';
import {
    ActiveQueryParam,
    PromotionTypes,
    PromotionTypesRegEx,
} from '../utils/const';
import { MomentSchema, yupCmsHistory } from '../utils/yup-schemas';
import { YupCategory } from './category';
import { YupOffer } from './offer';
import { RootStore } from './root';
import { WithNetworkConcurrency } from './with-network-concurrency';

const promotionItems = yup
    .object({
        title: yup.string().required(),
        subTitle: yup.string(),
        description: yup.mixed(),
        thumbnail: yup.string().nullable(),
        outsideActivatableHours: yup.string().nullable(),
        previewImageUrl: yup
            .string()
            .nullable()
            .test(
                'required-if-preview',
                'Preview image is required when preview mode is enabled',
                function (value) {
                    const hasPreview =
                        this.parent.hasPreview ||
                        this.options.context?.hasPreview;
                    if (!hasPreview) return true;
                    return !!value;
                }
            ),
        previewTitle: yup.string().nullable(),
    })
    .required()
    .shape({
        language: yup.string<Lang>().required().matches(LangRegEx),
    });

const YupPromotionAvailability = yup.object({
    id: yup.number().nullable(),
    dayOfWeek: yup.number().required(),
    startHour: yup.number().required(),
    endHour: yup.number().required(),
    dailyLimit: yup.number().required(),
    startDate: new MomentSchema().nullable(),
    endDate: new MomentSchema().nullable(),
});

/**
 * Yup schema for backend availability request (UTC format)
 */
export const YupPromotionAvailabilityRequest = yup.object({
    id: yup.number().nullable().optional(),
    dayOfWeek: yup.number().min(0).max(6).required(), // 0=Sunday, 6=Saturday
    startHour: yup.number().min(0).max(23).required(), // UTC hour (0-23)
    endHour: yup.number().min(0).max(23).required(), // UTC hour (0-23)
    dailyLimit: yup.number().min(0).required(),
    startDate: yup
        .string()
        .matches(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)
        .required(), // UTC ISO string
    endDate: yup
        .string()
        .matches(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)
        .required(), // UTC ISO string
});

export const YupBusinessUnit = yup.object({
    id: yup.number().required(),
    name: yup.string().required(),
});

export const YupProvince = yup.object({
    id: yup.number().required(),
    name: yup.string().required(),
    businessUnit: YupBusinessUnit.required(),
});

export const YupBusinessUnitProvince = yup.object({
    id: yup.number().required(),
    province: YupProvince.required(),
});

export type BusinessUnit = yup.InferType<typeof YupBusinessUnit>;
export type Province = yup.InferType<typeof YupProvince>;
export type BusinessUnitProvince = yup.InferType<
    typeof YupBusinessUnitProvince
>;

// Form-specific validation schema for provinces (simplified structure)
export const YupPromotionFormProvince = yup.object({
    businessUnitId: yup.number().nullable(),
    provinceId: yup.number().required(),
});

export const YupPromotion = yup
    .object({
        timezone: yup.string().required(),
        startDate: new MomentSchema().required(),
        endDate: new MomentSchema().required(),
        activationReadyDate: new MomentSchema().nullable(),
        deactivationDisplayDate: new MomentSchema().nullable(),
        ctDeepLink: yup.string().nullable(),
        ckDeepLink: yup.string().nullable(),
        id: yup.number().required(),
        isForMajor: yup.boolean().required(),
        isHint: yup.boolean().nullable(),
        isForCTConnect: yup.boolean().nullable(),
        isForCarwash: yup.boolean(),
        isKillerPromo: yup.boolean(),
        isForRoadToRewards: yup.boolean(),
        isForFuelTester: yup.boolean(),
        isForLabatt: yup
            .boolean()
            .test(
                'requires-private',
                'Labatt promotions must be private',
                function (value) {
                    if (value === true) {
                        return this.parent.isPrivate === true;
                    }
                    return true;
                }
            ),
        isActive: yup.boolean().required(),
        isVisibleOnHomePage: yup.boolean().required(),
        isPrivate: yup.boolean().nullable(),
        canBeDeleted: yup.boolean().required(),
        status: yup.string(),
        discountValue: yup.number().notRequired().nullable(),
        rebateAmount: yup.number().required(),
        originalPrice: yup.number().required(),
        totalActivations: yup.number().notRequired().nullable(),
        totalGrants: yup.number().notRequired().nullable(),
        activationCode: yup
            .string()
            .nullable()
            .test('shouldBeDefined', 'It should be defined', function (v) {
                const type: PromotionTypes = this.resolve(yup.ref('type'));
                return !(type === 'coupon' && !v);
            }),
        quantity: yup
            .number()
            .required()
            .nullable()
            .test('shouldBeDefined', 'It should be defined', function (v) {
                const type: PromotionTypes = this.resolve(yup.ref('type'));
                return !(type === 'coupon' && v == null);
            }),
        activationsCount: yup.number().nullable().default(0),
        type: yup
            .string<PromotionTypes>()
            .matches(PromotionTypesRegEx)
            .required(),
        promotionalOfferId: yup.number().nullable(),
        promotionalOffer: YupOffer.nullable(),
        items: yup.array(promotionItems).required(),
        category: YupCategory.required(),
        categoryId: yup.number().required(),
        cmsUserEmail: yup.string().notRequired().nullable(),
        cmsUserHistory: yup.array(yupCmsHistory).nullable(),
        temperatureActivationFloor: yup.number().nullable(),
        temperatureActivationCeiling: yup.number().nullable(),
        inTestingCampaign: yup.boolean(),
        hasPreview: yup.boolean(),
        autoReissueEnabled: yup.boolean().required(),
        availabilities: yup.array(YupPromotionAvailability).nullable(),
        availabilityIdsToDelete: yup.array(yup.number()).nullable(),
        promotionProvinces: yup.array(YupBusinessUnitProvince).nullable(),
    })
    .required();

// Form-specific validation schema (uses simplified provinces structure)
export const YupPromotionForm = YupPromotion.concat(
    yup.object({
        provinces: yup
            .array(YupPromotionFormProvince)
            .required()
            .min(1, 'At least one province must be selected'),
        activationCode: yup
            .string()
            .nullable()
            .test('shouldBeDefined', 'It should be defined', function (v) {
                const type: PromotionTypes = this.resolve(yup.ref('type'));
                return !(type === 'coupon' && !v);
            })
            .test(
                'upcAFormat',
                'Activation code must be exactly 12 digits (UPC-A format)',
                function (v) {
                    const type: PromotionTypes = this.resolve(yup.ref('type'));
                    if (type !== 'coupon' || !v) return true;
                    return /^\d{12}$/.test(v);
                }
            ),
        activationReadyDate: new MomentSchema()
            .nullable()
            .test(
                'required-if-deactivation-set',
                'Activation ready date is required if deactivation display date is set',
                (
                    value: unknown,
                    ctx: { parent?: { deactivationDisplayDate?: unknown } }
                ) => {
                    const deactivation = ctx?.parent?.deactivationDisplayDate;
                    if (deactivation == null) return true;
                    return value != null;
                }
            ),
        deactivationDisplayDate: new MomentSchema()
            .nullable()
            .when('activationReadyDate', {
                is: (val: unknown) => val != null,
                then: (schema: yup.Schema<unknown>) =>
                    schema.required(
                        'Deactivation display date is required if activation ready date is set'
                    ),
                otherwise: (schema: yup.Schema<unknown>) => schema.nullable(),
            }),
    })
);

const YupPromotionNetworkList = yup.object({
    page: yup.number().required(),
    perPage: yup.number().required(),
    total: yup.number().required(),
    results: yup.array(YupPromotion).default([]),
});

const YupNotPaginatedList = yup.object({
    total: yup.number().required(),
    results: yup.array(YupPromotion).default([]),
});

/**
 * Lenient item schema for the coupon-list response only. `mapPromotionsToCoupons`
 * (in `use-coupon-list.ts`) reads just `title` (with a blank-title fallback) and
 * `language` (to pick the localized item) per item, so only those two fields are
 * validated here, both nullable. Deliberately decoupled from the shared, strict
 * `promotionItems` schema above (`title` required, `subTitle`/`description`
 * non-nullable): real active coupons legitimately have null `subTitle` and
 * `description` (backend allows it), which the strict schema rejects, breaking
 * the Overview "Coupon activated" card. Unrecognized item fields (thumbnail,
 * description, subTitle, etc.) are simply not validated by yup and pass through.
 */
const YupCouponListItem = yup.object({
    title: yup.string().nullable(),
    language: yup.string<Lang>().nullable(),
});

/**
 * Lenient coupon row for the coupon-list response. Only `id` (the option value
 * and sort tie-break in `mapPromotionsToCoupons`) is required; `items` is
 * nullable to match `CouponSourceRow` in `use-coupon-list.ts`, which already
 * defaults a missing/absent `items` to `[]`.
 */
const YupCouponListItemRow = yup.object({
    id: yup.number().required(),
    isPrivate: yup.boolean().nullable(),
    isUnlimitedRedemption: yup.boolean().nullable(),
    items: yup.array(YupCouponListItem).nullable(),
});

export type CouponListRow = yup.InferType<typeof YupCouponListItemRow>;

/**
 * Coupon-list response schema for `fetchCoupons`. Unlike `YupNotPaginatedList`,
 * `results` is `.required()` with NO `.default([])`: a response missing the
 * `results` key (schema/backend drift) fails validation and surfaces as a network
 * error rather than being silently coerced to `[]` (which `useCouponList` would
 * misread as a soft "no coupons" empty state). A genuinely empty `results: []`
 * still validates and stays `empty`. Kept separate so `fetchForCategory` keeps its
 * lenient default-to-empty behavior unchanged.
 *
 * `results` items use `YupCouponListItemRow` (lenient, scoped to this response
 * only) rather than the shared strict `YupPromotion`, so this tolerance doesn't
 * broaden promotion create/edit validation elsewhere.
 */
export const YupCouponListResponse = yup.object({
    total: yup.number().required(),
    results: yup.array(YupCouponListItemRow).required(),
});

/**
 * Lenient item schema for the `/promotions/visible` response only. The pickers
 * fed by `promotionsForSelect` render `title` and `thumbnail` off an item and
 * read nothing else, so only those two are validated. Deliberately decoupled
 * from the shared, strict `promotionItems` above (`subTitle` and `description`
 * non-nullable): real grants legitimately carry null `subTitle` and null
 * `description` (backend allows it), which the strict schema rejects with
 * `grants[N].items[0].description cannot be null`, failing the whole loyalty
 * club page. `title` is nullable because the loyalty pickers already label a
 * title-less row `Promotion #<id>`.
 */
const YupVisiblePromotionItem = yup.object({
    title: yup.string().nullable(),
    thumbnail: yup.string().nullable(),
});

/**
 * Lenient promotion row for the `/promotions/visible` response only, carrying
 * exactly the fields its three pickers read: `id` (the option value everywhere),
 * `isActive` (featured-single filter), `isPrivate` and `type` (NHL private-coupon
 * filter, and the privacy rule on a club's unlimited-coupon row),
 * the `temperatureActivation*` bounds (reusable-coupon weather label),
 * `promotionProvinces[].province.name` (featured-single province eligibility),
 * and `items`. Required-vs-nullable matches the strict `YupPromotion` for each,
 * so the pickers keep the guarantees they had; `type` is an unconstrained string
 * rather than the `PromotionTypes` union because the only test on it is
 * `=== 'coupon'`, and a promotion type this CMS has not been taught yet must not
 * fail the response. `items` defaults to `[]` so a row with no localized item
 * still loads. Every other field the endpoint sends goes unvalidated instead of
 * through `YupPromotion`, which keeps create/edit validation strict.
 */
const YupVisiblePromotionRow = yup.object({
    id: yup.number().required(),
    isActive: yup.boolean().required(),
    isPrivate: yup.boolean().nullable(),
    type: yup.string().required(),
    temperatureActivationFloor: yup.number().nullable(),
    temperatureActivationCeiling: yup.number().nullable(),
    promotionProvinces: yup
        .array(
            yup.object({
                province: yup
                    .object({ name: yup.string().nullable() })
                    .nullable(),
            })
        )
        .nullable(),
    items: yup.array(YupVisiblePromotionItem).default([]),
});

export type VisiblePromotionRow = yup.InferType<typeof YupVisiblePromotionRow>;

export const YupVisiblePromotionsList = yup
    .object({
        grants: yup.array(YupVisiblePromotionRow).required(),
        promotions: yup.array(YupVisiblePromotionRow).required(),
    })
    .required();

const YupLabattPromotion = yup
    .object({
        id: yup.number().required(),
        title: yup.string().required(),
        subTitle: yup.string().nullable(),
        thumbnail: yup.string().nullable(),
    })
    .strict();

const YupLabattPromotionsList = yup.array(YupLabattPromotion).required();

export type PromotionList = yup.InferType<typeof YupPromotionNetworkList>;
export type Promotion = yup.InferType<typeof YupPromotion>;

export type PromotionFormProvince = {
    businessUnitId: number | null; // null for new selections
    provinceId: number;
};

export type PromotionForm = Omit<Partial<Promotion>, 'items' | 'provinces'> & {
    items: {
        title: string;
        subTitle?: string;
        description: string;
        thumbnail?: string | null;
        background?: string | null;
        backgroundColor?: string | null;
        promotedBannerTextColor?: string | null;
        outsideActivatableHours?: string | null;
        language: 'en' | 'fr';
        previewImageUrl?: string | null;
        previewTitle?: string | null;
    }[];
    activationReadyDate?: moment.Moment | null;
    deactivationDisplayDate?: moment.Moment | null;
    isPromotedBanner?: boolean;
    provinces: PromotionFormProvince[];
};
export type PromotionAvailability = yup.InferType<
    typeof YupPromotionAvailability
>;
export type PromotionAvailabilityRequest = yup.InferType<
    typeof YupPromotionAvailabilityRequest
>;
export type LabattPromotion = yup.InferType<typeof YupLabattPromotion>;

export type PromotionSearchParams = {
    perPage?: number;
    page?: number;
    type?: PromotionTypes;
    filter?: ActiveQueryParam;
    categoryId?: number;
    promotionalOfferId?: number;
    q?: string;
    isPrivate?: string;
    filteredProvinces?: string;
};

export class PromotionStore extends WithNetworkConcurrency {
    @observable promotions: PromotionList | null = null;
    @observable isFetchingCount = 0;
    @observable isUpsertingPromotion = false;
    @observable private cachedPromotion: Promotion | null = null;
    @observable isFetchingForSelect = false;
    @observable promotionsForSelect: VisiblePromotionRow[] = [];
    @observable promotionsLabattForSelect: LabattPromotion[] = [];
    @observable isDeletingAndRefreshing = false;
    @observable isFetchingForCategory = false;
    @observable promotionsForCategory: Promotion[] = [];
    @observable isExportingPromotions = false;
    @observable isTriggeringReissue = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        super();
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    private setPromotions(this: PromotionStore, promotions: PromotionList) {
        this.promotions = promotions;
    }

    @action
    setCachedPromotion(this: PromotionStore, promotion: Promotion) {
        this.cachedPromotion = promotion;
    }

    @action
    async fetchPromotions(
        this: PromotionStore,
        searchParams: PromotionSearchParams
    ) {
        const provinces = this.rootStore.applicationSelectionStore
            .getProvincesFromSelectedApplication;
        if (provinces) {
            searchParams.filteredProvinces = provinces;
        }

        const tag = this.getTag();
        this.isFetchingCount++;
        try {
            const response = await this.rootStore.makeNetworkCall(
                {
                    method: HttpMethod.GET,
                    url: '/promotions',
                    params: searchParams,
                },
                YupPromotionNetworkList
            );

            if (!response.err && this.isLatestTag(tag)) {
                this.setPromotions(response.data);
            }
        } finally {
            runInAction(() => {
                this.isFetchingCount--;
            });
        }
    }

    @action
    async fetchForSelect(this: PromotionStore) {
        this.isFetchingForSelect = true;
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/promotions/visible',
            },
            YupVisiblePromotionsList
        );
        this.isFetchingForSelect = false;

        if (!response.err) {
            // Combine grants and promotions for selection
            this.promotionsForSelect = [
                ...response.data.grants,
                ...response.data.promotions,
            ];
            return true;
        }
        return false;
    }

    /**
     * Fetches the coupons for the analytics coupon selector from the dedicated
     * `GET /analytics/coupons/options` endpoint: active, non-RPP coupons already
     * scoped and projected server-side to id + localized item titles only. We use
     * it instead of `GET /promotions?type=coupon&all=true` because that
     * management route is ungated and returns full promotion DTOs (activation
     * codes and all) to any Overview viewer, and loads every promotion; the
     * options endpoint is `/analytics/*`-gated (Customer-visible, matching the
     * Overview) and returns only what the dropdown needs. Returns the raw rows
     * (id + localized items, validated leniently against `YupCouponListResponse`
     * -- see there) so the caller maps them to selector options, or null on a
     * network error. Read-only: unlike `fetchPromotions`, it does not mutate any
     * shared observable, so it is safe from a card hook. See PR ctd-web-cms#304.
     */
    @action
    async fetchCoupons(this: PromotionStore): Promise<CouponListRow[] | null> {
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/analytics/coupons/options',
            },
            // `results` is required here (no default-to-empty): a malformed
            // response surfaces as an error rather than a false-empty coupon list.
            YupCouponListResponse
        );

        if (response.err || !response.data) {
            return null;
        }
        return response.data.results;
    }

    @action
    async fetchForCategory(this: PromotionStore, categoryId: number) {
        this.isFetchingForCategory = true;
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/promotions',
                params: { all: true, categoryId, filter: 'active' },
            },
            YupNotPaginatedList
        );
        this.isFetchingForCategory = false;

        if (!response.err) {
            this.promotionsForCategory = response.data.results;
        }
    }

    @action
    async fetchLabattPromotions(this: PromotionStore) {
        this.isFetchingForSelect = true;
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: '/promotions/labatt',
            },
            YupLabattPromotionsList
        );
        this.isFetchingForSelect = false;

        if (!response.err) {
            this.promotionsLabattForSelect = response.data;
        }
    }

    @action
    async fetchSinglePromotion(
        this: PromotionStore,
        promotionId: number
    ): Promise<Promotion> {
        if (promotionId === this.cachedPromotion?.id) {
            return toJS(this.cachedPromotion);
        }

        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: `/promotions/${promotionId}`,
            },
            YupPromotion
        );

        if (!response.err) {
            return response.data;
        } else {
            throw response.err;
        }
    }

    @action
    async createPromotion(this: PromotionStore, promotion: Promotion) {
        const cleanPromotion = produce(promotion, (draft: { id?: number }) => {
            delete draft.id;
        });

        this.isUpsertingPromotion = true;
        const response = await this.rootStore.makeNetworkCall<{
            id: number;
        }>({
            method: HttpMethod.POST,
            data: cleanPromotion,
            url: `/promotions`,
        });
        this.isUpsertingPromotion = false;

        if (!response.err) {
            NotificationManager.showSuccess('Promotion created');
            return response.data?.id ?? null;
        } else {
            throw response.err;
        }
    }

    @action
    async modifyPromotion(this: PromotionStore, promotion: Promotion) {
        this.isUpsertingPromotion = true;
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PUT,
            data: promotion,
            url: `/promotions/${promotion.id}`,
        });
        this.isUpsertingPromotion = false;

        if (!response.err) {
            NotificationManager.showSuccess('Promotion modified');
            return true;
        } else {
            throw response.err;
        }
    }

    @action
    async delete(this: PromotionStore, promotionId: number) {
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.DELETE,
            url: `/promotions/${promotionId}`,
        });

        if (!response.err) {
            NotificationManager.showSuccess('Promotion deleted');
        }

        return response;
    }

    @action
    async deleteAndRefreshList(
        this: PromotionStore,
        promotionId: number,
        params: PromotionSearchParams
    ) {
        if (this.isDeletingAndRefreshing) {
            return;
        }

        this.isDeletingAndRefreshing = true;
        const response = await this.delete(promotionId);
        if (!response.err) {
            await this.fetchPromotions(params);
        }
        this.isDeletingAndRefreshing = false;
    }

    async exportPromotions(this: PromotionStore): Promise<void> {
        if (this.isExportingPromotions) {
            return;
        }

        this.isExportingPromotions = true;

        try {
            const baseUrl = import.meta.env.REACT_APP_API_URL || '';
            const url = `${baseUrl}/api-cms/promotions/export`;

            const headers = this.rootStore.getHeaders();

            const response = await fetch(url, {
                method: 'GET',
                headers,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(
                    `Export failed with status: ${response.status} - ${errorText}`
                );
            }

            const blob = await response.blob();

            const downloadUrl = window.URL.createObjectURL(
                new Blob([blob], { type: 'text/csv;charset=UTF-8' })
            );

            const contentDisposition = response.headers.get(
                'content-disposition'
            );
            const filenameMatch = contentDisposition?.match(
                /filename="?([^"]+)"?/
            );
            const filename = filenameMatch
                ? filenameMatch[1]
                : `promotions-export-${new Date()
                      .toISOString()
                      .slice(0, 10)}.csv`;

            const link = document.createElement('a');
            link.style.display = 'none';
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                window.URL.revokeObjectURL(downloadUrl);
                document.body.removeChild(link);
            }, 100);

            NotificationManager.showSuccess('Promotions exported successfully');
        } catch (error) {
            console.error('Export error:', error);

            if (error instanceof Error) {
                NotificationManager.showError(
                    `Failed to export promotions: ${error.message}`
                );
            } else {
                NotificationManager.showError('Failed to export promotions');
            }
        } finally {
            this.isExportingPromotions = false;
        }
    }

    @action
    async triggerReissue(
        this: PromotionStore,
        bypassHourCheck = false
    ): Promise<boolean> {
        if (this.isTriggeringReissue) {
            return false;
        }

        this.isTriggeringReissue = true;

        try {
            const response = await this.rootStore.makeNetworkCall<{
                success: boolean;
                message: string;
                result: {
                    reissuedActivations: number;
                    promotionsAffected: number;
                    clearedGrants: number;
                };
            }>({
                method: HttpMethod.POST,
                url: '/promotions/reissue/trigger',
                data: { bypassHourCheck },
            });

            if (!response.err) {
                const result = response.data;
                NotificationManager.showSuccess(
                    `Reissue completed: ${result.result.reissuedActivations} activations reissued, ${result.result.clearedGrants} grants cleared across ${result.result.promotionsAffected} promotions`
                );
                return true;
            } else {
                NotificationManager.showError('Reissue failed');
                return false;
            }
        } catch (error) {
            console.error('Reissue trigger error:', error);
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : 'Unknown error occurred';
            NotificationManager.showError(`Reissue failed: ${errorMessage}`);
            return false;
        } finally {
            this.isTriggeringReissue = false;
        }
    }
}
